import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useLayoutEffect, useRef,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';
import type { LangCode } from '../../types';

import { STRICTERDOM_ENABLED } from '../../config';
import { disableStrict, enableStrict } from '../../lib/fasterdom/stricterdom';
import buildClassName from '../../util/buildClassName';
import { setLanguage } from '../../util/langProvider';
import { isSameLanguageFamily, resolveLanguagePreference } from '../../util/languagePreference';
import { navigateBack } from './helpers/backNavigation';
import renderText from '../common/helpers/renderText';
import { getSuggestedLanguage } from './helpers/getSuggestedLanguage';

import useAsync from '../../hooks/useAsync';
import useFlag from '../../hooks/useFlag';
import useLang from '../../hooks/useLang';
import useLangString from '../../hooks/useLangString';
import useMediaTransition from '../../hooks/useMediaTransition';
import useMultiaccountInfo from '../../hooks/useMultiaccountInfo';

import Button from '../ui/Button';
import Loading from '../ui/Loading';

import blankUrl from '../../assets/blank.png';
import im68LogoPath from '../../assets/68im-logo.svg';

type StateProps =
  Pick<GlobalState, 'connectionState' | 'authState' | 'authQrCode'>
  & { language?: LangCode };

const DATA_PREFIX = 'safew://login?token=';
const QR_SIZE = 280;
const QR_CODE_MUTATION_DURATION = 50; // The library is asynchronous and we need to wait for its mutation code

let qrCodeStylingPromise: Promise<typeof import('qr-code-styling')>;

function ensureQrCodeStyling() {
  if (!qrCodeStylingPromise) {
    qrCodeStylingPromise = import('qr-code-styling');
  }
  return qrCodeStylingPromise;
}

const AuthQrCode: FC<StateProps> = ({
  connectionState,
  authState,
  authQrCode,
  language,
}) => {
  const {
    returnToAuthPhoneNumber,
    returnToAuthEmailPhoneNumber,
    setSettingOption,
  } = getActions();

  const suggestedLanguage = getSuggestedLanguage();
  const lang = useLang();
  // eslint-disable-next-line no-null/no-null
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const languageRequestIdRef = useRef(0);

  const isConnected = connectionState === 'connectionStateReady';
  const continueText = useLangString(isConnected ? suggestedLanguage : undefined, 'ContinueOnThisLanguage', true);
  const [isLoading, markIsLoading, unmarkIsLoading] = useFlag();
  const [isQrMounted, markQrMounted, unmarkQrMounted] = useFlag();
  const hasActiveAccount = Object.keys(useMultiaccountInfo()).length > 0;

  const { result: qrCode } = useAsync(async () => {
    const QrCodeStyling = (await ensureQrCodeStyling()).default;
    return new QrCodeStyling({
      width: QR_SIZE,
      height: QR_SIZE,
      image: blankUrl,
      margin: 10,
      type: 'svg',
      dotsOptions: {
        type: 'rounded',
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
      },
      imageOptions: {
        imageSize: 0.4,
        margin: 8,
      },
      qrOptions: {
        errorCorrectionLevel: 'M',
      },
    });
  }, []);

  const transitionClassNames = useMediaTransition(isQrMounted);

  useLayoutEffect(() => {
    if (!authQrCode || !qrCode || !isConnected) {
      qrCodeRef.current?.replaceChildren();
      unmarkQrMounted();
      return undefined;
    }

    const container = qrCodeRef.current!;
    const data = `${DATA_PREFIX}${authQrCode.token}`;
    let restoreTimeoutId: number | undefined;

    try {
      if (STRICTERDOM_ENABLED) {
        disableStrict();
      }

      qrCode.update({
        data,
      });

      if (!isQrMounted) {
        qrCode.append(container);
        markQrMounted();
      }
    } finally {
      if (STRICTERDOM_ENABLED) {
        restoreTimeoutId = window.setTimeout(() => {
          enableStrict();
        }, QR_CODE_MUTATION_DURATION);
      }
    }

    return () => {
      if (restoreTimeoutId !== undefined) {
        window.clearTimeout(restoreTimeoutId);
        if (STRICTERDOM_ENABLED) {
          enableStrict();
        }
      }
    };
  }, [isConnected, authQrCode, isQrMounted, markQrMounted, unmarkQrMounted, qrCode]);

  useEffect(() => {
    if (isConnected) {
      void setLanguage(resolveLanguagePreference(language) as LangCode);
    }
  }, [isConnected, language]);

  const handleLangChange = useCallback(() => {
    markIsLoading();

    const requestId = ++languageRequestIdRef.current;
    void setLanguage(suggestedLanguage, () => {
      if (requestId !== languageRequestIdRef.current) {
        return;
      }

      unmarkIsLoading();
      setSettingOption({ language: suggestedLanguage });
    });
  }, [markIsLoading, setSettingOption, suggestedLanguage, unmarkIsLoading]);

  const handleReturnToAuthPhoneNumber = useCallback(() => {
    returnToAuthPhoneNumber();
  }, [returnToAuthPhoneNumber]);

  const handleReturnToAuthEmailPhoneNumber = useCallback(() => {
    returnToAuthEmailPhoneNumber();
  }, [returnToAuthEmailPhoneNumber]);

  const isAuthReady = authState === 'authorizationStateWaitQrCode';

  return (
    <div id="auth-qr-form" className="custom-scroll">
      {hasActiveAccount && (
        <Button
          size="smaller"
          round
          color="translucent"
          className="auth-close"
          ariaLabel="Back"
          onClick={navigateBack}
        >
          <i className="icon icon-close" />
        </Button>
      )}
      <div className="auth-form qr">
        <div className="qr-outer">
          <div
            className={buildClassName('qr-inner', transitionClassNames)}
            key="qr-inner"
          >
            <div
              key="qr-container"
              className="qr-container"
              ref={qrCodeRef}
              style={`width: ${QR_SIZE}px; height: ${QR_SIZE}px`}
            />
            <img
              src={im68LogoPath}
              className="qr-plane"
              alt=""
              width={54}
              height={54}
            />
          </div>
          {!isQrMounted && <div className="qr-loading"><Loading /></div>}
        </div>
        <h1>{lang('Login.QR.Title')}</h1>
        <ol>
          <li><span>{lang('Login.QR.Help1')}</span></li>
          <li><span>{renderText(lang('Login.QR2.Help2'), ['simple_markdown'])}</span></li>
          <li><span>{lang('Login.QR.Help3')}</span></li>
        </ol>
        {isAuthReady && (
          <>
            <Button isText onClick={handleReturnToAuthPhoneNumber}>{lang('Login.QR.Cancel')}</Button>
            <Button isText onClick={handleReturnToAuthEmailPhoneNumber}>{lang('Login.QR.EmailLogin')}</Button>
          </>
        )}
        {suggestedLanguage && !isSameLanguageFamily(suggestedLanguage, language) && continueText && (
          <Button isText isLoading={isLoading} onClick={handleLangChange}>{continueText}</Button>
        )}
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => {
    const {
      connectionState, authState, authQrCode, settings: { byKey: { language } },
    } = global;

    return {
      connectionState,
      authState,
      authQrCode,
      language,
    };
  },
)(AuthQrCode));
